/**
 * E43 · the client hub's Sleep & activity — the query's field list and the pure shaping of the day table (cc-health,
 * chapter "Health Data — Client hub: Sleep & activity (portal, E43)").
 */
export const SLEEP_NIGHT_FIELDS = ['night', 'inBedMinutes', 'asleepMinutes', 'awakenings', 'efficiency'];
export const ACTIVITY_DAY_FIELDS = ['date', 'stepCount', 'activeMinutes'];
export const HEART_DAY_FIELDS = ['date', 'restingHeartRate', { hrv: ['value', 'metric'] }];

export const TIMELINE_FIELDS = [
  'subject',
  'days',
  'sharedTypes',
  { nights: SLEEP_NIGHT_FIELDS },
  { activityDays: ACTIVITY_DAY_FIELDS },
  { heartRateDays: HEART_DAY_FIELDS },
  { moodByDay: ['day', 'mood'] },
  { scores: ['occurredAt', 'instrument', 'score'] },
];

export type TimelineNight = {
  night: string;
  inBedMinutes: number;
  asleepMinutes: number;
  awakenings: number;
  efficiency: number | null;
};
export type TimelineActivityDay = { date: string; stepCount: number; activeMinutes: number };
export type TimelineHeartDay = {
  date: string;
  restingHeartRate: number | null;
  hrv: { value: number; metric: string } | null;
};
export type TimelineRow = {
  subject: string;
  days: number;
  sharedTypes: string[];
  nights: TimelineNight[] | null;
  activityDays: TimelineActivityDay[] | null;
  heartRateDays: TimelineHeartDay[] | null;
  moodByDay: Array<{ day: string; mood: number }>;
  scores: Array<{ occurredAt: string; instrument: 'phq9' | 'gad7'; score: number }>;
};

export type DayLine = {
  day: string;
  /** the night that ENDED this morning (began the evening before) */
  night: TimelineNight | null;
  activity: TimelineActivityDay | null;
  heart: TimelineHeartDay | null;
  mood: number | null;
  scores: Array<{ instrument: 'phq9' | 'gad7'; score: number }>;
};

const dayBefore = (day: string): string => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
};

/** One line per day that has anything, newest first; a night sits on the day it ended. */
export const dayLinesOf = (row: TimelineRow): DayLine[] => {
  const days = new Set<string>();
  const nightByEnd = new Map<string, TimelineNight>();
  for (const n of row.nights ?? []) {
    const [y, m, d] = n.night.split('-').map(Number);
    const end = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
    nightByEnd.set(end, n);
    days.add(end);
  }
  for (const a of row.activityDays ?? []) days.add(a.date);
  for (const h of row.heartRateDays ?? []) days.add(h.date);
  for (const m of row.moodByDay) days.add(m.day);
  for (const s of row.scores) days.add(s.occurredAt.slice(0, 10));
  return Array.from(days)
    .sort((a, b) => (a < b ? 1 : -1))
    .map((day) => ({
      day,
      night: nightByEnd.get(day) ?? null,
      activity: row.activityDays?.find((a) => a.date === day) ?? null,
      heart: row.heartRateDays?.find((h) => h.date === day) ?? null,
      mood: row.moodByDay.find((m) => m.day === day)?.mood ?? null,
      scores: row.scores
        .filter((s) => s.occurredAt.slice(0, 10) === day)
        .map((s) => ({ instrument: s.instrument, score: s.score })),
    }));
};

export const average = (xs: number[]): number | null =>
  xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null;

export { dayBefore };
